import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';

import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges, changed } from '@frontend/sports/common/core/utils/simple-change';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';

import { DropdownOption } from '../../dropdown/models';
import { MarketHelpComponent } from '../market-help/market-help.component';
import { MultiColumnDropdownComponent } from './multi-column-dropdown.component';
import { ScorecastFooterComponent } from './scorecast-footer.component';
import {
    ANY_OTHER_SCORE_OPTION_KEY,
    CLASS_LINE_SWITCHER_SWAP_DIALOG,
    EventDetailsType,
    MultiColumnDropdownOption,
    ScorecastStorageItem,
} from './scorecast-option-group.model';
import { ScorecastWincastBaseComponent } from './scorecast-wincast-base.component';

@Component({
    selector: 'ms-scorecast-option-group',
    templateUrl: './scorecast-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [MarketHelpComponent, MultiColumnDropdownComponent, ScorecastFooterComponent],
})
export class ScorecastOptionGroupComponent extends ScorecastWincastBaseComponent implements OnChanges {
    @Input() marketTitle?: string;
    @Input() optionTitle?: string;
    @Input() override betDetails = '';

    options: MultiColumnDropdownOption[] = [];
    anyOtherOption: MultiColumnDropdownOption;
    selectedOption: DropdownOption;

    get warningMessage(): string {
        return this.messages.warningMessage
            ? interpolateDoubleBracesString(this.messages.warningMessage, {
                  Market: this.selectedPlayer?.name || '-',
                  Option: this.selectedOption?.name || '-',
              })
            : this.messages.warningMessageKey;
    }

    ngOnChanges(changes: ISimpleChanges<ScorecastOptionGroupComponent>): void {
        if (!this.event) {
            return;
        }

        if (changed(changes.event)) {
            this.mapParticipantImages();
        }

        this.playerMap = this.getPlayersMap();
        this.players = this.mapPlayerGroups();
        this.options = this.scorecastService.getDefaultScores(this.event.participants);
        this.anyOtherOption = {
            id: ANY_OTHER_SCORE_OPTION_KEY,
            name: this.messages.anyOtherScore,
            disabled: true,
            payload: [],
        };

        this.preSelectOptions();

        if (!this.preSelected) {
            const lowestPriceOption = this.getLowestPriceOption();
            const fixtureParticipantId = this.getFixtureParticipantById(lowestPriceOption.optionGroupId);

            this.setSelectedPlayer(this.getActiveWithFallback(this.players, (dropdownOption) => dropdownOption.id === fixtureParticipantId));
            this.selectedOption = this.hasScore(lowestPriceOption.name)
                ? this.getActiveWithFallback(this.options, (dropdownOption) => this.matchByName(lowestPriceOption, dropdownOption))
                : this.anyOtherOption;
        }

        this.setActiveOption();
    }

    onPlayerDropdownClick(expanded: boolean): void {
        this.onDropdownClick(expanded, EventDetailsType.Goalscorer);
    }

    override onPlayerChange(market: DropdownOption): void {
        this.setSelectedPlayer(market);
        super.onPlayerChange(market);
    }

    onOptionDropdownClick(expanded: boolean): void {
        this.onDropdownClick(expanded, EventDetailsType.Score);
    }

    onOptionChange(option: DropdownOption): void {
        this.selectedOption = option;
        this.scorecastService.trackSelect(option.name!, EventDetailsType.Score, this.optionGroup.periodName!);

        this.setActiveOption();
        this.saveItemsToStorage(this.mapStorageItems());
        this.restoreScrollPosition();
    }

    protected override setActiveOption(): void {
        this.activeEventOption = this.optionGroup?.options.find(
            (option) =>
                option.optionGroupId.toString() === this.playerMap[this.selectedPlayer.id as number] &&
                this.replaceDelimterValues(option.name).toLowerCase().includes(this.replaceDelimterValues(this.selectedOption.name!).toLowerCase()),
        );
    }

    protected override initSelectedOptionsFromPick(): void {
        this.dropdownMenuViewport = CLASS_LINE_SWITCHER_SWAP_DIALOG;

        const option = this.getEventOptionFromPick();
        if (!option) {
            return;
        }

        const fixtureParticipantId = this.getFixtureParticipantById(option.optionGroupId);
        if (!fixtureParticipantId) {
            return;
        }

        this.setSelectedPlayer(this.getPlayerById(fixtureParticipantId)!);

        if (!this.hasScore(option.name)) {
            this.selectedOption = this.anyOtherOption;
        } else {
            this.selectedOption = this.options
                .flatMap((dropdownGroup) => dropdownGroup.payload)
                .find((dropdownOption) => this.matchByName(option, dropdownOption))!;
        }

        this.preSelected = !!this.selectedOption;
    }

    protected override initSelecedOptionsFromStorage(): void {
        const persistedSelection = this.getPersistedSelectionFromStorage();

        if (persistedSelection) {
            this.setSelectedPlayer(this.getPlayerById(persistedSelection.player)!);

            if (persistedSelection.option === ANY_OTHER_SCORE_OPTION_KEY) {
                this.selectedOption = this.anyOtherOption;
            } else {
                this.selectedOption = this.options.flatMap((group) => group.payload).find((o) => o.id === persistedSelection.option)!;
            }

            this.preSelected = !!this.selectedOption;
        }
    }

    protected override mapStorageItems(): ScorecastStorageItem {
        return {
            id: this.optionGroup.id,
            player: this.selectedPlayer.id.toString(),
            option: this.selectedOption.id.toString(),
            expiryDate: Date.now() + this.cacheDuration,
        };
    }

    private setSelectedPlayer(player: DropdownOption): void {
        this.selectedPlayer = player;

        if (this.optionGroup?.options.length > 0) {
            this.activateOptions(this.optionGroup);
        }
    }

    private activateOptions(optionGroup: EventOptionGroup | undefined): void {
        const optionGroupId: string = this.playerMap[this.selectedPlayer.id as number];

        if (!optionGroup || !optionGroupId) {
            this.options?.forEach((dropdownOptionGroup) => dropdownOptionGroup.payload.forEach((dropdownOption) => (dropdownOption.disabled = true)));

            return;
        }

        const options = this.optionGroup.options
            .sort(this.sortByNativePrice)
            .filter((option) => option.optionGroupId.toString() === optionGroupId && option.visible);

        const anyOption = this.optionGroup.options.find((o) => o.optionGroupId.toString() === optionGroupId && !this.hasScore(o.name));

        if (anyOption) {
            this.anyOtherOption = {
                id: ANY_OTHER_SCORE_OPTION_KEY,
                name: anyOption.name.replace(this.selectedPlayer.name!, ''),
                disabled: !anyOption?.visible,
                payload: [],
            };
        }

        this.options?.forEach((dropdownOptionGroup) =>
            dropdownOptionGroup.payload.forEach((dropdownOption) => {
                dropdownOption.disabled = !options
                    .filter((activeOption) => isDefined(activeOption.name))
                    .some((activeOption) => this.matchByName(activeOption, dropdownOption));
            }),
        );
    }
}
